<script setup>
import { ref, computed, onMounted } from 'vue';
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import Tree from 'vue3-treeview';
import "vue3-treeview/dist/style.css";
import useVuelidate from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import { useStore } from 'vuex';
import axios from 'axios';
import { authHeader } from './../../../../helpers/authservice/auth-header';
import { roleService } from './../../../../helpers/authservice/role.service';
import { useRoute } from 'vue-router';
import moment from 'moment';
import { permissions } from '../../../../helpers/constants';
import _, { cloneDeep, isEqual } from 'lodash';
import Swal from 'sweetalert2';

const store = useStore();
const route = useRoute();

const title = ref("Role and Permission Settings")
const items = ref([
  {
      text: "Home",
      href: "/"
    },
      {
      text: "User Management",
    },
    {
      text: "Roles and Permissions Settings",
      active: true,
    }
])
const notification = computed(() => store.state.notification);

const loading = computed(() => store.state.globals.loading);

const submitted = ref(false);

const role = ref({
    name: '',
    description: '',
    is_superadmin: false,
    user_type: '',
    updated_at: null,
    user_count: 0
});

const userTypes = ref([]);

const nameSlug = computed(() => {
    const parts = [role.value.user_type, role.value.name].filter(Boolean);
    return parts.join(' ').trim().toLowerCase().replace(/\s+/g, '-');
});

const rolePermissions = ref([]);

 const rules = {
    name: {
        required: helpers.withMessage("Role Name is required", required)
    },
    user_type: {
        required: helpers.withMessage("User Type is required", required)
    },
 }

 const config = ref({
    roots: [
        "dashboard", "announcement", "forecast-menu", "general-search-menu", 
        "traffic-records-menu", "adjudication-menu", "lgu-menu", "ovr-menu", "user-management-menu", "accounting-menu",
        "camera-menu", "nov-menu", "payment-menu", "delivery-menu", "tracking-menu", "apprehension-menu",
        "reports-menu", "settings-menu"],
    checkboxes: true,
    padding: 25,
    checkMode: 0
});

const nodes = ref(permissions);

const existingCheckedPermissions = ref([]);

const copyExistingCheckedPermissions = ref([]);

const processExistingPermissions = () => {
    let array = [];
    if (rolePermissions.value && rolePermissions.value.length) {
        rolePermissions.value.forEach((permission) => {
            array = [ ...array, permission.id ]
        })
    }

    existingCheckedPermissions.value = array;
    copyExistingCheckedPermissions.value = cloneDeep(existingCheckedPermissions.value);
}

const loadCheckedPermissions = () => {
    for (const val in permissions) {
        if (permissions[val]._id !== null) {
            nodes.value[val].state = { checked: false }
        }

        if (_.includes(copyExistingCheckedPermissions.value, permissions[val]._id)) {
            nodes.value[val].state = { checked: true }
        }
    }
}

const checkPermissions = (node) => {
    switch (node._id) {
        case null:
            if (node.children.length) {
                for (const slug of node.children) {
                    existingCheckedPermissions.value = [ ...existingCheckedPermissions.value, nodes.value[slug]._id ]
                }
            }
            break;
        default:
            existingCheckedPermissions.value = [ ...existingCheckedPermissions.value, node._id ]
    }
}

const uncheckPermissions = (node) => {
    switch (node._id) {
        case null:
            if (node.children.length) {
                for (const slug of node.children) {
                    existingCheckedPermissions.value = [ ...existingCheckedPermissions.value, nodes.value[slug]._id ]
                    existingCheckedPermissions.value = _.remove(existingCheckedPermissions.value, function(n) {
                        return n !== nodes.value[slug]._id;
                    })
                }
            }
            break;
        default:
            existingCheckedPermissions.value = _.remove(existingCheckedPermissions.value, function(n) {
                return n !== node._id;
            })
    }
}

const updatePermissions = () => {
    if (isEqual(existingCheckedPermissions.value, copyExistingCheckedPermissions.value)) return;

    try {
         Swal.fire({
            title: "Modify Permissions",
            text: "Do you want to continue?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#34c38f",
            cancelButtonColor: "#f46a6a",
            confirmButtonText: "Yes",
        }).then(async (res) => {
            if (res.value) {
                await store.dispatch('role/updatePermissions', {  
                    id: role.value.id,
                    permissions: existingCheckedPermissions.value 
                });

                copyExistingCheckedPermissions.value = cloneDeep(existingCheckedPermissions.value);
            }
        });
    } catch (err) {
        const message = err?.response?.data?.message || 'An error occurred';
        store.dispatch('notification/error', message, { root: true });
    }
}

const resetToOriginal = () => {
    if (isEqual(existingCheckedPermissions.value, copyExistingCheckedPermissions.value)) return;

    existingCheckedPermissions.value = copyExistingCheckedPermissions.value;
    loadCheckedPermissions();
}

const v$ = useVuelidate(rules, role);

const getRoleDetails = async () => {
    store.dispatch('globals/loading', null, { root: true });

    try {
        const { data } = await axios.get(`/roles/${route.params.id}`, {
            headers: authHeader()
        })
        role.value = data.data;

        store.dispatch('globals/loaded', null, { root: true });
    } catch (err) {
        const message = err?.response?.data?.message || 'An error occurred';
        store.dispatch('notification/error', message, { root: true });
        store.dispatch('globals/loaded', null, { root: true });
    }
}

const getRolePermissions = async () => {
    store.dispatch('globals/loading', null, { root: true });

    try {
        const { data } = await axios.get(`/roles/${route.params.id}/permissions`, {
            headers: authHeader()
        })
        rolePermissions.value = data.data;

        store.dispatch('globals/loaded', null, { root: true });
    } catch (err) {
        const message = err?.response?.data?.message || 'An error occurred';
        store.dispatch('notification/error', message, { root: true });
        store.dispatch('globals/loaded', null, { root: true });
    }
}

const updateRole = async () => {
    try {
        submitted.value = true
        v$.value.$touch();

        if (v$.value.$invalid) return;
        await store.dispatch('role/updateRole', role.value);
    } catch (err) {
        const message = err?.response?.data?.message || 'An error occurred';
        store.dispatch('notification/error', message, { root: true });
    }
}

const formattedDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('YYYY-MM-DD h:mm A')
}

onMounted(async () => {
    await getRoleDetails();
    await getRolePermissions();
    processExistingPermissions();
    loadCheckedPermissions();

    try {
        const data = await roleService.getUserTypes();
        userTypes.value = data.data;
    } catch (err) {
        const message = err?.response?.data?.message || 'Failed to load user types';
        store.dispatch('notification/error', message, { root: true });
    }
})
</script>

<template>
  <Layout>
    <PageHeader back="/user-management/roles/list" :title="title" :items="items" />
    <div class="row">
        <div class="col-md-12 col-lg-6">
                <b-overlay :show="loading">
                <div class="card">
                    <div class="card-header">
                        <div class="d-flex justify-content-between">
                            <h4 class="card-title mb-0">Edit Role Information</h4>
                            <div>
                                <div class="text-muted">User Count: {{ role.user_count }}</div>
                                <div class="text-muted">Last Updated: {{ formattedDate(role.updated_at) }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col lg-12">
                                <div class="row">
                                    <div class="col md-10">
                                        <form @submit.prevent="updateRole" class="mx-2">
                                            <b-alert v-if="notification && notification.message"
                                                show variant="success" class="fade show" role="alert">
                                                {{ notification.message }}
                                            </b-alert>

                                            <div v-if="notification.messages.length > 0">
                                                <b-alert v-for="(message, index) in notification.messages" :key="index"
                                                    show variant="danger" class="fade show" role="alert">
                                                    {{ message }}
                                                </b-alert>
                                            </div>
                                            <div class="row">
                                                <p class="fs-6 text text-decoration-underline">Role Information</p>
                                                <div class="col-md-12">
                                                    <div class="mb-3">
                                                        <label for="name" class="form-label required">Role Name</label>
                                                        <input
                                                            autofocus
                                                            :class="{ 'is-invalid': submitted && v$.name.$error }"
                                                            v-model="role.name" type="text" class="form-control" placeholder="Enter role name..." id="name" />
                                                        <div class="form-text text-muted">
                                                            <i class="mdi mdi-information-outline"></i> Format: <strong>Dept RoleName</strong> &mdash; slug will combine User Type + Name
                                                        </div>
                                                        <div v-if="nameSlug" class="form-text text-muted">
                                                            Slug: <code>{{ nameSlug }}</code>
                                                        </div>
                                                        <div
                                                            v-for="item of v$.name.$errors"
                                                            :key="item.$uid"
                                                            class="invalid-feedback"
                                                        >
                                                            <span v-if="item.$message">{{ item.$message }}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="col-md-12">
                                                    <div class="mb-3">
                                                        <label for="description" class="form-label">Role Description</label>
                                                        <textarea
                                                            v-model="role.description" class="form-control" placeholder="Enter role description..." id="description"></textarea>
                                                    </div>
                                                </div>
                                                <div class="col-md-12">
                                                    <div class="mb-3">
                                                        <label for="userType" class="form-label required">User Type</label>
                                                        <select v-model="role.user_type" :class="{ 'is-invalid': submitted && v$.user_type.$error }" class="form-select" id="userType">
                                                            <option value="">Select user type...</option>
                                                            <option v-for="type in userTypes" :key="type" :value="type">{{ type }}</option>
                                                        </select>
                                                        <div v-for="item of v$.user_type.$errors" :key="item.$uid" class="invalid-feedback">
                                                            <span v-if="item.$message">{{ item.$message }}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="col-md-12">
                                                    <div class="mb-3">
                                                        <label class="form-label d-block">Super Admin</label>
                                                        <div class="form-check form-switch">
                                                            <input
                                                                class="form-check-input"
                                                                type="checkbox"
                                                                role="switch"
                                                                id="is_superadmin"
                                                                v-model="role.is_superadmin" />
                                                            <label class="form-check-label" for="is_superadmin">
                                                                <span v-if="role.is_superadmin" class="badge bg-success">Super Admin</span>
                                                                <span v-else class="text-muted">Grant full access to all LGUs</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="row">
                                                <div class="col-lg-12">
                                                    <div class="text-end">
                                                         <router-link to="/user-management/roles/list" class="btn btn-danger mr-3">
                                                            <i class="ri-close-fill fs-4 mr-1 valign-middle"></i>
                                                            Cancel
                                                        </router-link>
                                                        <button type="submit" class="btn btn-success">
                                                            <i class="ri-save-2-fill fs-4 mr-1 valign-middle"></i>
                                                            Update Role
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </b-overlay>
        </div>
            
        <div class="col-md-12 col-lg-6">
            <b-overlay :show="loading">
                <div class="card">
                        <div class="card-header">
                        <h4 class="card-title mb-0">Modify Permissions Settings</h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-12">
                                <div class="text-end">
                                    <button :disabled="isEqual(existingCheckedPermissions, copyExistingCheckedPermissions)" @click.prevent="resetToOriginal" type="button" class="btn btn-danger mr-3">
                                        <i class="bx bx-reset fs-4 mr-1 valign-middle"></i>
                                        Reset
                                    </button>
                                    <button :disabled="isEqual(existingCheckedPermissions, copyExistingCheckedPermissions)" @click.prevent="updatePermissions" type="button" class="btn btn-success">
                                        <i class="ri-save-2-fill fs-6 mr-1 valign-middle"></i>
                                        Update Permissions
                                    </button>
                                </div>
                            </div>
                            <div class="col-md-12">
                                <Tree :config="config" :nodes="nodes" @nodeChecked="checkPermissions" @nodeUnchecked="uncheckPermissions" />
                            </div>
                            <div class="col-md-12">
                                <div class="text-end">
                                    <button :disabled="isEqual(existingCheckedPermissions, copyExistingCheckedPermissions)" @click.prevent="resetToOriginal" type="button" class="btn btn-danger mr-3">
                                        <i class="bx bx-reset fs-4 mr-1 valign-middle"></i>
                                        Reset
                                    </button>
                                    <button :disabled="isEqual(existingCheckedPermissions, copyExistingCheckedPermissions)" @click.prevent="updatePermissions" type="button" class="btn btn-success">
                                        <i class="ri-save-2-fill fs-6 mr-1 valign-middle"></i>
                                        Update Permissions
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </b-overlay>
        </div>
    </div>
  </Layout>
</template>
